import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../hooks/useReports';
import { useAuthStore } from '../../stores/auth.store';

interface SalesDataPoint {
  month: string;
  total: number;
  shop?: string;
}

interface StaffPerformance {
  username: string;
  total_sales: number;
  invoice_count: number;
}

interface PaymentDist {
  type: string;
  total: number;
  count: number;
}

export default function ManagerDashboard() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const { data: dashboard, isLoading } = useDashboard();

  if (isLoading) {
    return (
      <div className="page">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  const monthlySales = (dashboard?.monthly_sales as SalesDataPoint[]) ?? [];
  const staffPerformance = (dashboard?.staff_performance as StaffPerformance[]) ?? [];
  const paymentDist = (dashboard?.payment_distribution as PaymentDist[]) ?? [];

  // Simple bar chart using CSS
  const maxSale = Math.max(...monthlySales.map((d) => d.total), 1);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('dashboard', 'Dashboard')}</h1>
        <span className="page-subtitle">Account: {account?.id}</span>
      </div>

      {/* Monthly sales chart */}
      <div className="dashboard-card">
        <h2 className="dashboard-card__title">Monthly Sales</h2>
        {monthlySales.length === 0 ? (
          <p className="text-muted">No sales data available.</p>
        ) : (
          <div className="bar-chart">
            {monthlySales.map((d, i) => (
              <div key={i} className="bar-chart__bar-group">
                <div
                  className="bar-chart__bar"
                  style={{ height: `${(d.total / maxSale) * 100}%` }}
                  title={`${d.month}: ${d.total}`}
                />
                <span className="bar-chart__label">{d.month}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dashboard-row">
        {/* Staff performance */}
        <div className="dashboard-card dashboard-card--half">
          <h2 className="dashboard-card__title">Staff Performance</h2>
          {staffPerformance.length === 0 ? (
            <p className="text-muted">No data.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('user', 'Staff')}</th>
                  <th>Sales</th>
                  <th>Invoices</th>
                </tr>
              </thead>
              <tbody>
                {staffPerformance.map((s) => (
                  <tr key={s.username}>
                    <td>{s.username}</td>
                    <td>{s.total_sales}</td>
                    <td>{s.invoice_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Payment distribution */}
        <div className="dashboard-card dashboard-card--half">
          <h2 className="dashboard-card__title">Payment Methods</h2>
          {paymentDist.length === 0 ? (
            <p className="text-muted">No data.</p>
          ) : (
            <div className="payment-dist">
              {paymentDist.map((p) => (
                <div key={p.type} className="payment-dist__row">
                  <span className="payment-dist__type">{p.type}</span>
                  <span className="payment-dist__count">{p.count} txns</span>
                  <span className="payment-dist__total">{p.total}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
